import React, { useState, useEffect, useRef, useCallback } from 'react';
import SEO from '../components/SEO';

const C = {
  bg: '#0a0e17',
  bgPanel: '#0f1420',
  bgHover: '#1a2332',
  border: '#1e2a3f',
  text: '#c8d4e8',
  textDim: '#7a8699',
  textBright: '#e8ecf5',
  orange: '#ff6b00',
  orangeDim: '#cc5500',
  green: '#00e676',
  red: '#ff3d3d',
  yellow: '#ffb800',
  blue: '#2196f3',
  mono: "'JetBrains Mono', 'Consolas', 'Courier New', monospace",
};

// ── Data ──

const NAV = [
  { title: 'Getting Started', items: [
    { id: 'welcome', label: 'Welcome' },
    { id: 'first-login', label: 'Your First Login' },
    { id: 'interface', label: 'Interface Overview' },
    { id: 'guided-tour', label: 'Guided Tour' },
  ]},
  { title: 'Core Concepts', items: [
    { id: 'workspaces', label: 'Workspaces' },
    { id: 'panels', label: 'Panels' },
    { id: 'customization', label: 'Customization' },
    { id: 'analytics-workflow', label: 'Analytics Workflow' },
  ]},
  { title: 'Feature Guides', items: [
    { id: 'scanner', label: 'Live Scanner' },
    { id: 'charts', label: 'Charts & Technicals' },
    { id: 'watchlist', label: 'Watchlist & Alerts' },
    { id: 'earnings', label: 'Earnings Tools' },
    { id: 'options-flow', label: 'Options Flow' },
    { id: 'intelligence', label: 'Geopolitical Intel' },
    { id: 'research-ai', label: 'Research AI' },
    { id: 'portfolio', label: 'Portfolio Tracking' },
  ]},
  { title: 'Reference', items: [
    { id: 'panel-reference', label: 'All Panels' },
    { id: 'shortcuts', label: 'Keyboard Shortcuts' },
    { id: 'notifications', label: 'Notifications' },
    { id: 'settings', label: 'Settings' },
  ]},
  { title: 'Help', items: [
    { id: 'faq', label: 'FAQ' },
    { id: 'troubleshooting', label: 'Troubleshooting' },
    { id: 'support', label: 'Contact Support' },
  ]},
];

const WORKSPACES = [
  { name: 'Trading Floor', desc: 'Daily market overview.', detail: 'Discord trading chat, Fear & Greed index, Intelligence Wire, Newsdesk, macro signals, economic calendar, market radar, sector heatmap, watchlist, COT positioning, oil prices, and earnings calendar. This is your morning coffee workspace.' },
  { name: 'Analytics', desc: 'Deep analysis of a single symbol.', detail: 'Type any ticker into the search bar and every panel updates (chart, key metrics, technicals, company profile, symbol news, AI research assistant, screener, portfolio, earnings). This is where you drill down.' },
  { name: 'Scanner', desc: 'Live trading signals.', detail: 'Streaming scanners with 46 pre-built strategies, top list leaderboard, chart, and custom scan builder. Built for day traders who need to see setups the moment they trigger.' },
  { name: 'Intelligence', desc: 'Geopolitical insight.', detail: 'Intelligence wire, AI world brief, strategic risk matrix, Country Instability Index (CII), signal intelligence, geographic convergence, DEFCON status, infrastructure cascade, and the research assistant. For understanding the bigger picture.' },
  { name: 'News Desk', desc: 'News from everywhere.', detail: 'World news, live news TV (Bloomberg, Sky, CNN, Al Jazeera), regional feeds, think tanks, government press, crisis watch, and research assistant. A pure information firehose.' },
  { name: 'Global Risk', desc: 'Security and physical risk.', detail: 'Interactive 2D map with togglable layers, earthquakes, active disasters, protests, DEFCON, military tracking, maritime intelligence, cyber threat actors, and research assistant. When you want to see where trouble is brewing.' },
  { name: 'Crypto & Alt', desc: 'Crypto and alternative markets.', detail: 'Crypto watchlist, stablecoin monitor, BTC ETF flows, crypto Fear & Greed, prediction markets, market radar, supply chain risk, intelligence wire, social sentiment, and research assistant.' },
  { name: 'Macro Deep', desc: 'Macroeconomic analysis.', detail: 'Yield curve, FRED indicators, central bank rates, supply chain, COT positioning, macro signals, economic calendar, company explorer, screener, portfolio, and research assistant. For traders who trade the macro picture.' },
];

const BULLISH = ['Volume Breakout','Gap & Go','Golden Cross','10 SMA Pullback','Opening Power Bar','New 52-Week High','Consolidation Breakout','RSI Oversold Bounce','VWAP Reclaim','MACD Bullish Cross','Block Buy Print','Relative Strength Leader','Gap Fill','Bollinger Squeeze','Short Squeeze Setup','Premarket Gapper Up','Premarket Volume Leader','Premarket News Catalyst'];
const BEARISH = ['Volume Breakdown','Gap Down Continuation','Death Cross','New 52-Week Low','RSI Overbought Reversal','VWAP Rejection','MACD Bearish Cross','Block Sell Print','Bearish Power Bar','Breakdown Stabilization','Relative Weakness','Bollinger Breakdown','Premarket Gapper Down','Premarket Dump'];
const NEUTRAL = ['Unusual Volume','Halt & Resume','Pre-Market Mover','After-Hours Mover','Bull Flag','Bear Flag','RSI Divergence','MFI Divergence','Closing Range Breakout','Running New Highs','Unusual Social Mentions','Earnings Reaction','Sector Rotation','Premarket Range Builder'];

const PANEL_SECTIONS = [
  { title: 'Markets (21)', panels: [
    { n: 'Watchlist', d: 'Personal watchlist with live prices and price alerts' },
    { n: 'Chart', d: 'Live candlestick chart with indicators and timeframe switching' },
    { n: 'S&P 500 Heatmap', d: 'Treemap of ~120 stocks by sector, sized by market cap' },
    { n: 'Earnings Calendar', d: 'Upcoming, this week, and historical earnings' },
    { n: 'Fear & Greed', d: 'CNN Fear & Greed Index with gauge visualization' },
    { n: 'Yield Curve', d: 'US Treasury curve 1M-30Y with historical overlays' },
    { n: 'Market Radar', d: '7-signal momentum indicator with BUY/CASH verdict' },
    { n: 'Central Bank Rates', d: 'Rates from 20+ central banks' },
    { n: 'COT Positioning', d: 'CFTC Commitments of Traders, weekly' },
    { n: 'Sector Heatmap', d: '11 GICS sectors with daily performance' },
    { n: 'Market Sentiment', d: 'Live broker positioning long/short' },
    { n: 'Stablecoin Monitor', d: 'USDT, USDC, DAI supply and peg status' },
    { n: 'BTC ETF Flows', d: 'Daily spot Bitcoin ETF inflows and outflows' },
    { n: 'Oil & Energy', d: 'WTI, Brent, Natural Gas prices' },
    { n: 'Supply Chain Risk', d: 'Shipping delays, port congestion, PMIs' },
    { n: 'Company Explorer', d: 'Full company profile with 8 tabs' },
    { n: 'Stock Screener', d: 'AI natural language stock screener' },
    { n: 'Portfolio Intelligence', d: 'Track positions with AI daily briefs' },
    { n: 'Key Metrics', d: 'Adaptive metrics per asset type' },
    { n: 'Technical Analysis', d: 'Trend, RSI, ATR, Bollinger, support/resistance' },
    { n: 'API Access', d: 'Data Export API documentation and keys' },
  ]},
  { title: 'Scanner (3, multi-instance)', panels: [
    { n: 'Scanner', d: '46 pre-built strategies, multi-instance' },
    { n: 'Top List', d: 'Real-time leaderboard, sortable, multi-instance' },
    { n: 'Scan Builder', d: 'Build custom scans with 20+ fields' },
  ]},
  { title: 'Options Flow (6)', panels: [
    { n: 'Live Options Flow', d: 'Streaming options flow with filters' },
    { n: 'Flow Deep Dive', d: 'Per-ticker 5-tab analysis' },
    { n: 'Flow Sentiment', d: 'Multi-timeframe bull/bear gauge' },
    { n: 'Top Flow Movers', d: 'Stocks ranked by premium or net flow' },
    { n: 'Whale Tracker', d: 'Only $1M+ premium trades' },
    { n: 'Multi-Timeframe', d: 'Sentiment cards across timeframes' },
  ]},
  { title: 'Intelligence (14)', panels: [
    { n: 'Intelligence Wire', d: 'Severity-classified news from 300+ sources' },
    { n: 'FlockTrade Newsdesk', d: 'Proprietary news feed with alerts' },
    { n: 'AI World Brief', d: 'AI-generated world analysis' },
    { n: 'AI Deduction', d: 'Forward-looking scenario planning' },
    { n: 'Country Brief', d: 'AI-generated country briefs' },
    { n: 'Strategic Risk', d: 'Cross-domain threat matrix' },
    { n: 'Country Instability Index', d: '24-country stability scores' },
    { n: 'Signal Intelligence', d: 'Patterns in OSINT/SIGINT/HUMINT' },
    { n: 'Geographic Convergence', d: 'Signal clustering by region' },
    { n: 'Infrastructure Cascade', d: 'Disruption propagation modeling' },
    { n: 'Research Assistant', d: 'Conversational AI with terminal data access' },
    { n: 'Live Intelligence', d: 'Real-time intelligence feed' },
    { n: 'Intel Feed', d: 'Curated intelligence feed' },
    { n: 'Focal Points', d: 'Key areas of attention' },
  ]},
  { title: 'Macro (4)', panels: [
    { n: 'Macro Signals', d: 'Composite bullish/bearish/neutral verdict' },
    { n: 'Economic Calendar', d: 'Events with impact filters and history' },
    { n: 'Prediction Markets', d: 'Polymarket probabilities' },
    { n: 'FRED Indicators', d: 'Unemployment, inflation, GDP, confidence' },
  ]},
  { title: 'News (10)', panels: [
    { n: 'World News', d: 'Global news aggregator from 50+ sources' },
    { n: 'Middle East (MENA)', d: 'Regional news feed' },
    { n: 'Asia-Pacific', d: 'Regional news feed' },
    { n: 'Europe', d: 'Regional news feed' },
    { n: 'Africa', d: 'Regional news feed' },
    { n: 'Latin America', d: 'Regional news feed' },
    { n: 'Think Tanks', d: 'RAND, Brookings, IISS, SIPRI, Carnegie' },
    { n: 'Government Feeds', d: 'Official government press releases' },
    { n: 'Tech & AI', d: 'Technology and cybersecurity news' },
    { n: 'Crisis Watch', d: 'ICG conflict escalation/de-escalation' },
  ]},
  { title: 'Geo Risk (16)', panels: [
    { n: 'DEFCON Status', d: 'US DEFCON level with change alerts' },
    { n: 'Pizza Index', d: 'Pentagon delivery activity tracker' },
    { n: 'Seismic Monitor', d: 'M4.5+ earthquakes with tsunami warnings' },
    { n: 'Active Disasters', d: 'Fires, floods, volcanoes, storms' },
    { n: 'Military Tracking', d: 'Aircraft, naval, troop movements' },
    { n: 'Maritime Intel', d: 'Tanker and naval vessel tracking' },
    { n: 'Cyber Threat Actors', d: 'APT groups and campaigns' },
    { n: 'Social Unrest', d: 'Protests and civil instability' },
    { n: 'Climate Anomalies', d: 'Extreme weather and records' },
    { n: 'Nuclear Facilities', d: 'Nuclear plants and operational status' },
    { n: 'Aviation Intel', d: 'Military and significant aviation' },
    { n: 'Displacement Tracker', d: 'UNHCR refugee and IDP movements' },
    { n: 'Orbital Surveillance', d: 'Satellites, debris, launches' },
    { n: 'Critical Minerals', d: 'Rare earths and supply dependency' },
    { n: 'Internet Outages', d: 'Connectivity disruptions worldwide' },
    { n: 'Weather Warnings', d: 'Severe weather from met services' },
  ]},
  { title: 'Social (4)', panels: [
    { n: 'Social Intelligence', d: 'Reddit and X trending topics' },
    { n: 'Trading Floor Chat', d: 'Discord bridge chat' },
    { n: 'Custom Monitors', d: 'User-configured monitoring feeds' },
    { n: 'Layoffs Tracker', d: 'Global tech and corporate layoffs' },
  ]},
  { title: 'Maps (2)', panels: [
    { n: 'Global Map 2D', d: 'Interactive Leaflet map with 15+ layers' },
    { n: 'Global Map 3D', d: '3D globe with same data layers' },
  ]},
  { title: 'Live Streams (2)', panels: [
    { n: 'Live News TV', d: 'Bloomberg, Sky, Euronews, CNN, Al Jazeera, more' },
    { n: 'Live Webcams', d: 'Conflict zones and strategic locations' },
  ]},
  { title: 'Other (7)', panels: [
    { n: 'WTO Trade Policy', d: 'Tariffs, trade conflicts, sanctions' },
    { n: 'BIS Central Bank Data', d: 'Bank for International Settlements' },
    { n: 'Gulf Economies', d: 'Gulf states economic indicators' },
    { n: 'Gulf FDI Tracker', d: 'Foreign investment in the Gulf' },
    { n: 'Stock Analysis', d: 'AI-powered stock analysis' },
    { n: 'Daily Market Brief', d: 'AI-generated daily summary' },
    { n: 'Breaking Alerts', d: 'History of all alerts shown' },
  ]},
];

const SHORTCUTS = [
  { group: 'Navigation', rows: [
    { keys: ['Ctrl','K'], action: 'Command palette' },
    { keys: ['Ctrl','1–8'], action: 'Switch workspace' },
    { keys: ['Ctrl',','], action: 'Settings' },
    { keys: ['Ctrl','/'], action: 'Help menu' },
    { keys: ['Esc'], action: 'Close modal or slide-out' },
  ]},
  { group: 'Panels', rows: [
    { keys: ['Ctrl','P'], action: 'Add panel' },
    { keys: ['Ctrl','F'], action: 'Search in active panel' },
    { keys: ['Ctrl','D'], action: 'Duplicate selected panel' },
    { keys: ['Ctrl','W'], action: 'Close selected panel' },
  ]},
  { group: 'Analytics', rows: [
    { keys: ['/'], action: 'Focus symbol search' },
    { keys: ['Ctrl','Enter'], action: 'Open symbol in Analytics' },
  ]},
  { group: 'Chart', rows: [
    { keys: ['+','/','−'], action: 'Zoom in / out' },
    { keys: ['←','/','→'], action: 'Scroll through time' },
    { keys: ['1','5','15','H','D'], action: 'Quick timeframe switch' },
  ]},
];

// ── Small components ──

const Callout = ({ type, title, children }) => {
  const color = type === 'tip' ? C.green : type === 'warn' ? C.yellow : C.blue;
  return (
    <div style={{ borderLeft: `3px solid ${color}`, padding: '16px 20px', margin: '20px 0', background: C.bgPanel, fontSize: 14 }}>
      <div style={{ fontFamily: C.mono, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6, fontWeight: 600, color }}>{title || type.toUpperCase()}</div>
      {children}
    </div>
  );
};

const Kbd = ({ children }) => (
  <span style={{ fontFamily: C.mono, background: C.bgPanel, border: `1px solid ${C.border}`, borderBottomWidth: 2, padding: '2px 8px', fontSize: 12, color: C.textBright, display: 'inline-block', margin: '0 2px' }}>{children}</span>
);

const Code = ({ children }) => (
  <code style={{ fontFamily: C.mono, background: C.bgPanel, border: `1px solid ${C.border}`, padding: '2px 6px', fontSize: 12, color: C.orange }}>{children}</code>
);

const PanelCard = ({ name, desc }) => {
  const [hover, setHover] = useState(false);
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ background: C.bgPanel, border: `1px solid ${hover ? C.orangeDim : C.border}`, padding: '14px 16px', transition: 'border-color 0.15s' }}
    >
      <div style={{ fontWeight: 600, color: C.textBright, fontSize: 14, marginBottom: 4 }}>{name}</div>
      <div style={{ fontSize: 12, color: C.textDim, lineHeight: 1.5 }}>{desc}</div>
    </div>
  );
};

const StrategyTags = ({ items, color }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
    {items.map(s => (
      <span key={s} style={{ background: C.bgPanel, border: `1px solid ${color}`, padding: '4px 10px', fontSize: 12, fontFamily: C.mono, color }}>{s}</span>
    ))}
  </div>
);

const Steps = ({ children }) => (
  <ol style={{ counterReset: 'step', listStyle: 'none', margin: '20px 0', padding: 0 }}>{children}</ol>
);

const Step = ({ title, children }) => (
  <li style={{ counterIncrement: 'step', paddingLeft: 48, position: 'relative', marginBottom: 20 }}>
    <span style={{
      position: 'absolute', left: 0, top: 0, width: 32, height: 32,
      background: C.orange, color: C.bg, fontWeight: 700, fontFamily: C.mono,
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14,
      content: 'counter(step)',
    }} />
    <strong style={{ color: C.textBright, display: 'block', marginBottom: 4 }}>{title}</strong>
    <span style={{ color: C.text }}>{children}</span>
  </li>
);

// Step numbers via CSS counter won't work with inline styles, so use explicit numbers
const NumberedSteps = ({ steps }) => (
  <div style={{ margin: '20px 0' }}>
    {steps.map((s, i) => (
      <div key={i} style={{ paddingLeft: 48, position: 'relative', marginBottom: 20 }}>
        <span style={{
          position: 'absolute', left: 0, top: 0, width: 32, height: 32,
          background: C.orange, color: C.bg, fontWeight: 700, fontFamily: C.mono,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14,
        }}>{i + 1}</span>
        <strong style={{ color: C.textBright, display: 'block', marginBottom: 4 }}>{s.title}</strong>
        <span style={{ color: C.text }}>{s.desc}</span>
      </div>
    ))}
  </div>
);

// ── Main component ──

const Docs = () => {
  const [search, setSearch] = useState('');
  const [activeId, setActiveId] = useState('welcome');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const sectionRefs = useRef({});
  const mainRef = useRef(null);

  // Collect all section IDs
  const allIds = NAV.flatMap(g => g.items.map(i => i.id));

  // Scroll spy
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveId(entry.target.id);
        }
      });
    }, { rootMargin: '-20% 0px -70% 0px' });

    allIds.forEach(id => {
      const el = sectionRefs.current[id];
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const scrollTo = useCallback((id) => {
    const el = sectionRefs.current[id];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setSidebarOpen(false);
    }
  }, []);

  const setRef = useCallback((id) => (el) => {
    sectionRefs.current[id] = el;
  }, []);

  // Filter sections by search
  const q = search.toLowerCase().trim();

  const sectionVisible = (id) => {
    if (!q) return true;
    // Check if the section heading or nav label matches
    for (const g of NAV) {
      for (const item of g.items) {
        if (item.id === id) {
          if (item.label.toLowerCase().includes(q)) return true;
        }
      }
    }
    // Also match against the section content - check the ref's text
    const el = sectionRefs.current[id];
    if (el && el.textContent.toLowerCase().includes(q)) return true;
    return false;
  };

  const H2 = ({ children }) => <h2 style={{ fontSize: 24, color: C.textBright, fontWeight: 600, marginBottom: 16, paddingBottom: 12, borderBottom: `1px solid ${C.border}` }}>{children}</h2>;
  const H3 = ({ children }) => <h3 style={{ fontSize: 18, color: C.textBright, fontWeight: 600, margin: '32px 0 12px' }}>{children}</h3>;
  const H4 = ({ children }) => <h4 style={{ fontSize: 15, color: C.orange, fontWeight: 600, margin: '20px 0 8px', fontFamily: C.mono }}>{children}</h4>;
  const P = ({ children }) => <p style={{ marginBottom: 16, color: C.text, lineHeight: 1.6 }}>{children}</p>;
  const UL = ({ children }) => <ul style={{ margin: '12px 0 20px 24px', color: C.text, lineHeight: 1.6 }}>{children}</ul>;
  const LI = ({ children }) => <li style={{ marginBottom: 6 }}>{children}</li>;
  const B = ({ children }) => <strong style={{ color: C.textBright, fontWeight: 600 }}>{children}</strong>;
  const A = ({ href, children }) => <span onClick={() => { if (href.startsWith('#')) scrollTo(href.slice(1)); }} style={{ color: C.orange, cursor: 'pointer', borderBottom: `1px dotted ${C.orangeDim}` }}>{children}</span>;

  const sectionStyle = { marginBottom: 64, scrollMarginTop: 24 };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: C.bg, color: C.text, fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", fontSize: 14, lineHeight: 1.6 }}>
      <SEO
        title="Documentation — Terminal Guides & Reference"
        description="Guides and reference for the FlockTrade terminal: workspaces, panels, live scanner, charts, options flow, geopolitical intel, the AI research assistant, shortcuts and FAQ."
        path="/docs"
      />

      {/* Mobile toggle */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        style={{
          display: 'none', position: 'fixed', top: 12, left: 12, zIndex: 1001,
          background: C.bgPanel, border: `1px solid ${C.border}`, color: C.orange,
          padding: '8px 12px', fontFamily: C.mono, fontSize: 13, cursor: 'pointer',
        }}
        className="docs-sidebar-toggle"
      >
        {sidebarOpen ? '✕' : '☰'} DOCS
      </button>

      {/* Sidebar */}
      <aside style={{
        width: 280, minWidth: 280, background: C.bgPanel, borderRight: `1px solid ${C.border}`,
        padding: '24px 0', position: 'sticky', top: 0, height: '100vh', overflowY: 'auto',
        ...(sidebarOpen ? {} : {}),
      }}>
        <div style={{ padding: '0 24px 20px', borderBottom: `1px solid ${C.border}`, marginBottom: 20 }}>
          <div style={{ fontFamily: C.mono, fontSize: 16, fontWeight: 700, color: C.orange, letterSpacing: 0.5 }}>FLOCKTRADE</div>
          <div style={{ fontSize: 11, color: C.textDim, marginTop: 4, textTransform: 'uppercase', letterSpacing: 1 }}>Terminal Documentation</div>
        </div>

        <div style={{ margin: '0 24px 20px', position: 'relative' }}>
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: C.textDim, fontSize: 14 }}>⌕</span>
          <input
            type="text"
            placeholder="Search docs..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%', background: C.bg, border: `1px solid ${C.border}`, color: C.text,
              padding: '8px 12px 8px 32px', fontFamily: 'inherit', fontSize: 13, outline: 'none',
              boxSizing: 'border-box',
            }}
            onFocus={e => { e.target.style.borderColor = C.orange; }}
            onBlur={e => { e.target.style.borderColor = C.border; }}
          />
        </div>

        <nav>
          {NAV.map(group => {
            const visibleItems = group.items.filter(i => sectionVisible(i.id));
            if (q && visibleItems.length === 0) return null;
            return (
              <div key={group.title} style={{ marginBottom: 20 }}>
                <div style={{ padding: '8px 24px', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: C.textDim, fontWeight: 600 }}>{group.title}</div>
                {(q ? visibleItems : group.items).map(item => {
                  const isActive = activeId === item.id;
                  return (
                    <a
                      key={item.id}
                      href={`#${item.id}`}
                      onClick={e => { e.preventDefault(); scrollTo(item.id); }}
                      style={{
                        display: 'block', padding: '6px 24px', color: isActive ? C.orange : C.text,
                        textDecoration: 'none', fontSize: 13, borderLeft: `3px solid ${isActive ? C.orange : 'transparent'}`,
                        background: isActive ? C.bgHover : 'transparent', transition: 'all 0.15s',
                      }}
                      onMouseEnter={e => { if (!isActive) { e.currentTarget.style.background = C.bgHover; e.currentTarget.style.borderLeftColor = C.orangeDim; }}}
                      onMouseLeave={e => { if (!isActive) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderLeftColor = 'transparent'; }}}
                    >
                      {item.label}
                    </a>
                  );
                })}
              </div>
            );
          })}
        </nav>
      </aside>

      {/* Main content */}
      <main ref={mainRef} style={{ padding: '48px 64px', maxWidth: 900, flex: 1 }}>
        <div style={{ borderBottom: `1px solid ${C.border}`, paddingBottom: 24, marginBottom: 32 }}>
          <h1 style={{ fontSize: 32, color: C.textBright, fontWeight: 600, marginBottom: 8 }}>FlockTrade Terminal</h1>
          <div style={{ color: C.textDim, fontSize: 16 }}>Everything you need to know to get the most out of your terminal</div>
        </div>

        {/* WELCOME */}
        <section id="welcome" ref={setRef('welcome')} style={{ ...sectionStyle, display: sectionVisible('welcome') ? undefined : 'none' }}>
          <H2>Welcome to FlockTrade</H2>
          <P>FlockTrade Terminal is a Bloomberg-style market intelligence platform built for serious self-directed traders. It combines real-time market data, 40+ live scanning strategies, options flow, geopolitical intelligence, and AI research into a single customizable workspace.</P>
          <P>This documentation is designed to be read however you need it. Search at the top of the sidebar jumps straight to what you want. If you're new, start with <A href="#first-login">Your First Login</A> and work through Core Concepts. If you're looking for a specific feature, jump directly to it in the Feature Guides.</P>
          <Callout type="tip">Inside the terminal, click the <B>❓ icon</B> in the top bar at any time to open this documentation, start a guided tour, or see what's new.</Callout>
        </section>

        {/* FIRST LOGIN */}
        <section id="first-login" ref={setRef('first-login')} style={{ ...sectionStyle, display: sectionVisible('first-login') ? undefined : 'none' }}>
          <H2>Your First Login</H2>
          <P>When you first open the terminal, a guided tour starts automatically. It takes about 60 seconds and shows you the core layout. If you'd rather jump in and explore, click "Skip tour". You can always restart it later from the ❓ menu.</P>
          <P>After the tour, you'll land on <B>Trading Floor</B>, the default workspace. It's designed to give you a solid daily overview with Discord chat from your trading community, market sentiment, news wire, economic calendar, and a watchlist. From here you can switch to any of the other seven workspaces using the tabs at the top.</P>
          <Callout type="note">Your layout is saved automatically. Resize a panel, move it around, add or remove panels. Everything is remembered for your next login.</Callout>
        </section>

        {/* INTERFACE */}
        <section id="interface" ref={setRef('interface')} style={{ ...sectionStyle, display: sectionVisible('interface') ? undefined : 'none' }}>
          <H2>Interface Overview</H2>
          <P>The terminal has a few persistent elements you'll use constantly:</P>
          <H4>Top Bar</H4>
          <P>Along the top you have status indicators (<B>ONLINE</B> shows your connection is live), a bug report button, the notification bell <B>🔔</B>, the help menu <B>❓</B>, settings <B>⚙️</B>, a configurable clock, and an <B>EXIT</B> button that returns you to the main FlockTrade app.</P>
          <H4>Ticker Bar</H4>
          <P>Just below the top bar is a scrolling ticker with live prices for key symbols. Click any ticker to open its deep-dive popup.</P>
          <H4>Workspace Tabs</H4>
          <P>Eight tabs representing different workspaces. Click to switch between them. Each one is a curated set of panels for a specific task (trading, analysis, news, etc).</P>
          <H4>Add Panel Button</H4>
          <P>The <B>+ Panel</B> button on the left lets you add any of 90+ panel types to your current workspace.</P>
          <H4>Panel Grid</H4>
          <P>The main area is a customizable grid of panels. Drag headers to move, drag corners to resize. Click the <B>ℹ️</B> icon on any panel to see what it does.</P>
        </section>

        {/* GUIDED TOUR */}
        <section id="guided-tour" ref={setRef('guided-tour')} style={{ ...sectionStyle, display: sectionVisible('guided-tour') ? undefined : 'none' }}>
          <H2>Guided Tour</H2>
          <P>The terminal includes a step-by-step interactive tour that highlights each major element with a short description. It runs automatically on your first login, but you can restart it anytime.</P>
          <NumberedSteps steps={[
            { title: 'Open the help menu', desc: 'Click the ❓ icon in the top bar.' },
            { title: 'Start guided tour', desc: 'Click "Start guided tour" from the dropdown.' },
            { title: 'Follow the prompts', desc: 'Use "Next" to advance through 8 steps covering the ticker bar, workspaces, panels, notifications, and settings. Use "Back" to revisit any step, or "Skip" to exit.' },
          ]} />
          <Callout type="tip" title="Pro Tip">Pro tips appear contextually the first time you do certain things. For example, the first time you add a scanner panel, you'll see a tip about adding multiple scanners side by side.</Callout>
        </section>

        {/* WORKSPACES */}
        <section id="workspaces" ref={setRef('workspaces')} style={{ ...sectionStyle, display: sectionVisible('workspaces') ? undefined : 'none' }}>
          <H2>Workspaces</H2>
          <P>A workspace is a preconfigured layout of panels built around a specific task. Instead of building your own setup from scratch, start with a workspace and customize from there. All eight workspaces are fully customizable and your changes persist.</P>
          {WORKSPACES.map(w => (
            <div key={w.name} style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 20, padding: 16, background: C.bgPanel, border: `1px solid ${C.border}`, marginBottom: 12, alignItems: 'start' }}>
              <div style={{ fontFamily: C.mono, color: C.orange, fontWeight: 600, fontSize: 14 }}>{w.name}</div>
              <div style={{ color: C.text, fontSize: 13 }}><B>{w.desc}</B> {w.detail}</div>
            </div>
          ))}
          <Callout type="tip">You can switch workspaces with <Kbd>Ctrl</Kbd>+<Kbd>1</Kbd> through <Kbd>Ctrl</Kbd>+<Kbd>8</Kbd>. On Mac, use <Kbd>Cmd</Kbd> instead of <Kbd>Ctrl</Kbd>.</Callout>
        </section>

        {/* PANELS */}
        <section id="panels" ref={setRef('panels')} style={{ ...sectionStyle, display: sectionVisible('panels') ? undefined : 'none' }}>
          <H2>Panels</H2>
          <P>Panels are the building blocks of the terminal. Each one shows a specific type of data or tool. The terminal ships with over 90 panel types organized into categories: Markets, Scanner, Flow, Intel, Macro, News, Geo, Social, Map, Live, and more.</P>
          <H3>Adding a Panel</H3>
          <NumberedSteps steps={[
            { title: 'Click + Panel', desc: 'Found on the left side of the workspace bar.' },
            { title: 'Browse by category', desc: 'Panels are grouped by type. Use the search bar at the top of the panel picker to filter.' },
            { title: 'Click to add', desc: 'The new panel appears at the bottom of your workspace. Drag it where you want.' },
          ]} />
          <H3>Moving and Resizing</H3>
          <P>Drag the panel header (the bar at the top with the name) to move a panel. Drag the corners or edges to resize. The grid snaps to ensure panels align cleanly.</P>
          <H3>Removing a Panel</H3>
          <P>Click the <B>×</B> icon in the panel header. You can always add it back from the + Panel menu.</P>
          <H3>Multi-Instance Panels</H3>
          <P>Some panels (Scanner and Top List) can be added multiple times with different configurations. You might have one scanner panel running "Volume Breakout", another running "Gap & Go", and a third running your own custom scan, all visible side by side.</P>
          <Callout type="note">Every panel has an <B>ℹ️</B> icon in its header. Hover for a quick tooltip, click for a detailed explanation of what the panel does and how to use it.</Callout>
        </section>

        {/* CUSTOMIZATION */}
        <section id="customization" ref={setRef('customization')} style={{ ...sectionStyle, display: sectionVisible('customization') ? undefined : 'none' }}>
          <H2>Customization</H2>
          <P>The terminal is built to be reshaped to fit how you work. Everything you change is saved automatically.</P>
          <H3>Themes and Display</H3>
          <P>In Settings → Terminal Settings, you can adjust:</P>
          <UL>
            <LI><B>Default workspace</B>: which workspace opens when you log in</LI>
            <LI><B>Font size</B>: Compact, Normal, or Large</LI>
            <LI><B>Grid density</B>: 3 to 6 columns per row</LI>
            <LI><B>Animations</B>: toggle UI transitions on or off</LI>
          </UL>
          <H3>Export and Import</H3>
          <P>In Settings → Data & Privacy, you can export your entire configuration as a JSON file (watchlists, custom scans, workspace layouts, preferences) and import it on another device or share with a colleague.</P>
          <H3>Creating Custom Workspaces</H3>
          <P>Start from any existing workspace, customize it however you want, then save as a new workspace with your own name. Your custom workspaces appear alongside the eight defaults.</P>
        </section>

        {/* ANALYTICS WORKFLOW */}
        <section id="analytics-workflow" ref={setRef('analytics-workflow')} style={{ ...sectionStyle, display: sectionVisible('analytics-workflow') ? undefined : 'none' }}>
          <H2>Analytics Workflow</H2>
          <P>The <B>Analytics</B> workspace is built around one concept: pick a symbol, and every panel shows you data about it. This is the fastest way to do a full workup on a ticker.</P>
          <NumberedSteps steps={[
            { title: 'Switch to Analytics', desc: <>Click the Analytics tab, or press <Kbd>Ctrl</Kbd>+<Kbd>2</Kbd>.</> },
            { title: 'Enter a ticker', desc: <>Type a symbol in the search bar (or press <Kbd>/</Kbd> to focus it). Try <Code>AAPL</Code>, <Code>NVDA</Code>, <Code>BTC-USD</Code>, <Code>EUR/USD</Code>. Stocks, crypto, and forex all work.</> },
            { title: 'All panels update', desc: 'The chart loads live candles, key metrics show fundamentals, technical analysis runs, symbol news filters to that ticker, and the research assistant is primed to answer questions about it.' },
            { title: 'Ask the AI', desc: 'In the Research Assistant panel, ask anything like "Is this stock overbought?", "What\'s the recent analyst sentiment?", or "Compare to peers". It has access to all the other panels\' data.' },
          ]} />
        </section>

        {/* SCANNER */}
        <section id="scanner" ref={setRef('scanner')} style={{ ...sectionStyle, display: sectionVisible('scanner') ? undefined : 'none' }}>
          <H2>Live Scanner</H2>
          <Callout type="warn" title="Coming Soon">The Live Scanner is currently in final testing and will be enabled for all users shortly. The documentation below describes how it will work when it goes live. Scan Builder is available now for creating and saving your custom scan configurations, ready to activate the moment the scanner launches.</Callout>
          <P>The scanner is the feature that puts FlockTrade in Trade Ideas territory. It watches ~3,000 US stocks in real time and fires alerts the moment a pattern triggers. No refresh, no polling. The alert appears the instant the condition is met.</P>
          <H3>How It Works</H3>
          <P>Each scanner panel runs one strategy. You can have multiple scanner panels open simultaneously, each monitoring a different strategy. When a stock meets the strategy's conditions, it appears in that scanner's feed with the time, price, change, volume, and relative volume.</P>
          <H3>Adding a Scanner</H3>
          <NumberedSteps steps={[
            { title: 'Click + Panel', desc: 'Open the panel picker.' },
            { title: 'Choose Scanner category', desc: 'Pick "Scanner" from the list.' },
            { title: 'Pick a strategy', desc: 'A modal opens with all 46 strategies grouped into Bullish, Bearish, Neutral/Pattern, and My Custom Scans. Search or scroll, click the one you want.' },
            { title: 'Panel appears and starts streaming', desc: 'The panel title matches your chosen strategy. Alerts appear live as they happen.' },
          ]} />
          <H3>Available Strategies</H3>
          <H4>Bullish (18)</H4>
          <StrategyTags items={BULLISH} color={C.green} />
          <H4>Bearish (14)</H4>
          <StrategyTags items={BEARISH} color={C.red} />
          <H4>Neutral / Pattern (14)</H4>
          <StrategyTags items={NEUTRAL} color={C.yellow} />
          <H3>Custom Scans</H3>
          <P>The <B>Scan Builder</B> panel lets you create your own scans with any combination of 20+ fields (price, change, volume, RSI, SMA, VWAP, short float, RSI divergence, and more) and seven operators. Save your scans and they appear in the strategy selector under "My Custom Scans", ready to be added as a panel.</P>
          <H3>Changing a Scanner's Strategy</H3>
          <P>Click the <B>⚙ Change</B> button in any scanner panel header to switch strategies without removing and re-adding the panel. The title and icon update automatically.</P>
          <H3>Acting on Alerts</H3>
          <P>When a scanner fires an alert, click the symbol to open the Ticker Popup for a quick view. Click "Analytics" to open the full deep dive with chart, metrics, news, and AI analysis.</P>
          <Callout type="tip" title="Pro Tip">Add "All Bullish" or "All Bearish" scanner panels to see a combined feed from every strategy in that category. Useful for getting a sense of overall market tone.</Callout>
        </section>

        {/* CHARTS */}
        <section id="charts" ref={setRef('charts')} style={{ ...sectionStyle, display: sectionVisible('charts') ? undefined : 'none' }}>
          <H2>Charts & Technicals</H2>
          <P>The Chart panel streams live candlestick data tick-by-tick. Candles build in real time as trades hit the tape.</P>
          <H3>Timeframes</H3>
          <P>Switch between 1-minute, 5-minute, 15-minute, 1-hour, 4-hour, daily, and weekly views using the buttons at the top of the chart. Keyboard shortcuts: <Kbd>1</Kbd> for 1m, <Kbd>5</Kbd> for 5m, <Kbd>15</Kbd> for 15m, <Kbd>H</Kbd> for hourly, <Kbd>D</Kbd> for daily.</P>
          <H3>Indicators</H3>
          <P>Toggle the following from the chart controls:</P>
          <UL>
            <LI><B>SMA 20/50/200</B>: simple moving averages</LI>
            <LI><B>RSI (14)</B>: relative strength indicator, shown in sub-panel</LI>
            <LI><B>Volume bars</B>: color-coded by candle direction</LI>
            <LI><B>Price alert lines</B>: lines for any alerts you've set on this symbol</LI>
          </UL>
          <H3>Setting Price Alerts from the Chart</H3>
          <P>Right-click anywhere on the chart and choose "Set alert at this price". The alert fires when price hits that level and delivers via whatever channels you've configured in Settings (toast, banner, notification center, or browser push).</P>
          <H3>Technical Analysis Panel</H3>
          <P>For a full breakdown (trend direction, RSI status, ATR, Bollinger Band position, volume trends, support and resistance levels, and moving average alignment), add the <B>Technical Analysis</B> panel. It auto-updates with your active Analytics symbol.</P>
        </section>

        {/* WATCHLIST */}
        <section id="watchlist" ref={setRef('watchlist')} style={{ ...sectionStyle, display: sectionVisible('watchlist') ? undefined : 'none' }}>
          <H2>Watchlist & Price Alerts</H2>
          <P>The Watchlist panel keeps the symbols you care about in one place with live prices.</P>
          <H3>Adding Symbols</H3>
          <P>Click the <B>+</B> in the Watchlist panel and start typing. Autocomplete suggests matches as you type. Stocks, crypto, forex, and indices are all searchable. Click a result to add.</P>
          <H3>Live Prices</H3>
          <P>Prices update every 30 seconds, and live during market hours. Green and red flash briefly on price changes.</P>
          <H3>Setting Price Alerts</H3>
          <NumberedSteps steps={[
            { title: 'Click the 🔔 icon', desc: 'Next to any symbol in your watchlist.' },
            { title: 'Set the conditions', desc: 'Above or below a price level, percentage change threshold, or volume spike.' },
            { title: 'Choose delivery', desc: 'Toast, banner, notification center, or browser push. Configure defaults in Settings.' },
            { title: 'Alert fires automatically', desc: 'The moment price hits the trigger, all active delivery methods fire simultaneously.' },
          ]} />
          <H3>Removing Symbols</H3>
          <P>Hover over a symbol and click the × that appears. To remove multiple at once, enter edit mode from the panel menu.</P>
          <H3>Using Watchlist with Analytics</H3>
          <P>When you're on the Analytics workspace, clicking any symbol in the Watchlist updates every other panel to that symbol. This is the fastest way to cycle through your watchlist and do a quick workup on each one.</P>
        </section>

        {/* EARNINGS */}
        <section id="earnings" ref={setRef('earnings')} style={{ ...sectionStyle, display: sectionVisible('earnings') ? undefined : 'none' }}>
          <H2>Earnings Tools</H2>
          <P>The Earnings Calendar panel shows upcoming reports, this week's schedule, and historical earnings data.</P>
          <H3>Upcoming Tab</H3>
          <P>A chronological list of upcoming earnings. Filter by Today, This Week, Next Week, or All. Sort by date, market cap, or symbol. Each entry shows the date, time (BMO = before market open, AMC = after market close), EPS estimate, and revenue estimate.</P>
          <H3>This Week Tab</H3>
          <P>Five-column day-by-day view (Monday through Friday) with BMO and AMC splits. Today's column is highlighted. The biggest names in each slot appear first.</P>
          <H3>History Tab</H3>
          <P>Type any ticker to see its last 8 quarters of earnings with a visual bar chart (estimate vs actual), surprise percentages, revenue trend sparkline, beat rate summary, and detailed quarterly table.</P>
          <H3>Ticker Popup</H3>
          <P>Clicking any ticker (in the Earnings panel or anywhere else) opens the Ticker Popup. For earnings, it shows:</P>
          <UL>
            <LI>Beat rate (e.g., 7 of 8 quarters beat = 87.5%)</LI>
            <LI>Average EPS and revenue surprise</LI>
            <LI>8-quarter grouped bar chart</LI>
            <LI>Revenue trend</LI>
            <LI>Quarterly detail table</LI>
            <LI>Next earnings date with countdown and "Set Alert" button</LI>
          </UL>
          <H3>Earnings Alerts</H3>
          <P>Click "Set Alert" on the next earnings date and you'll be notified 60 minutes before the report (configurable in Settings → Alerts).</P>
        </section>

        {/* OPTIONS FLOW */}
        <section id="options-flow" ref={setRef('options-flow')} style={{ ...sectionStyle, display: sectionVisible('options-flow') ? undefined : 'none' }}>
          <H2>Options Flow</H2>
          <P>Live options flow tracks unusual options activity in real time. These are the large premium trades that often indicate institutional positioning.</P>
          <H3>Live Options Flow Panel</H3>
          <P>A streaming feed of every qualifying trade. Filter by:</P>
          <UL>
            <LI><B>All</B>: everything</LI>
            <LI><B>Whale $1M+</B>: only trades with $1M+ premium</LI>
            <LI><B>Sweeps</B>: multi-exchange sweeps (aggressive orders)</LI>
            <LI><B>0DTE</B>: zero days to expiry</LI>
            <LI><B>Unusual</B>: volume far above open interest</LI>
          </UL>
          <H3>Flow Deep Dive</H3>
          <P>Click any ticker in the flow to open the Flow Deep Dive popup with 5 tabs:</P>
          <UL>
            <LI><B>Overview</B>: summary stats, bullish/bearish regime, expected 1-day move</LI>
            <LI><B>Flow Wall</B>: strike-by-strike flow distribution, DTE distribution, side aggression</LI>
            <LI><B>OI & Levels</B>: open interest changes, support and resistance with flow context</LI>
            <LI><B>Gamma/GEX</B>: gamma exposure per strike, stabilization/acceleration regime</LI>
            <LI><B>Smart Money</B>: targets with confidence scores, 7-day recurring patterns, whale trades</LI>
          </UL>
          <H3>Related Panels</H3>
          <UL>
            <LI><B>Flow Sentiment</B>: multi-timeframe bull/bear gauge across Today, 7D, 30D</LI>
            <LI><B>Top Flow Movers</B>: tickers ranked by total premium or net flow</LI>
            <LI><B>Whale Tracker</B>: only $1M+ premium trades, gold-highlighted</LI>
            <LI><B>Multi-Timeframe</B>: per-ticker sentiment cards showing trend convergence or divergence</LI>
          </UL>
        </section>

        {/* INTELLIGENCE */}
        <section id="intelligence" ref={setRef('intelligence')} style={{ ...sectionStyle, display: sectionVisible('intelligence') ? undefined : 'none' }}>
          <H2>Geopolitical Intelligence</H2>
          <P>FlockTrade combines market data with geopolitical intelligence, something no other trading platform does. Understanding supply chains, conflict zones, and political risk matters for stock picks like NVDA (Taiwan exposure), energy stocks (Middle East), and agriculture (Ukraine, Russia).</P>
          <H3>Intelligence Wire</H3>
          <P>Aggregated news from 300+ sources classified by severity: <span style={{ color: C.red }}>CRITICAL</span> (immediate threat), <span style={{ color: C.orange }}>HIGH</span> (significant event), <span style={{ color: C.yellow }}>MEDIUM</span>, <span style={{ color: C.textDim }}>LOW</span>. Clickable headlines open full articles in a popup.</P>
          <H3>Country Instability Index (CII)</H3>
          <P>Real-time stability score for 24 monitored countries, updated every 15 minutes. Higher score = more unstable. Based on armed conflict, protests, economic crisis, sanctions, and natural disasters. Useful for assessing country-specific risk before entering positions.</P>
          <H3>AI World Brief & Country Brief</H3>
          <P>AI-generated summaries of world events with source scoring and threat assessment. The Country Brief provides a structured 150-word intelligence brief for any country: STATUS → RISKS → OUTLOOK → WATCH.</P>
          <H3>Strategic Risk Matrix</H3>
          <P>Threat matrix across military, economic, and cyber domains. Helps identify where convergent risks are building.</P>
          <H3>Geographic Convergence</H3>
          <P>Detects when multiple intelligence signals cluster in the same region. When military activity, protests, and economic stress all show up in one place, something is usually about to happen.</P>
          <H3>DEFCON Status</H3>
          <P>US DEFCON level (1-5) with automatic alerts when the level changes.</P>
        </section>

        {/* RESEARCH AI */}
        <section id="research-ai" ref={setRef('research-ai')} style={{ ...sectionStyle, display: sectionVisible('research-ai') ? undefined : 'none' }}>
          <H2>Research Assistant</H2>
          <P>The Research Assistant is a conversational AI with access to every piece of data in the terminal. Ask questions in natural language and it cross-references financials, news, technical indicators, and geopolitical intelligence to give you informed answers.</P>
          <H3>Good Questions to Ask</H3>
          <UL>
            <LI>"Is NVDA overbought right now?"</LI>
            <LI>"What's driving the move in energy stocks today?"</LI>
            <LI>"Compare AAPL vs MSFT on valuation metrics"</LI>
            <LI>"Summarize the geopolitical risks affecting TSM"</LI>
            <LI>"What's the sentiment on semiconductors this week?"</LI>
            <LI>"Is the yield curve signaling recession?"</LI>
            <LI>"Explain this options flow pattern"</LI>
          </UL>
          <Callout type="note">The Research Assistant is not a financial advisor. It provides analysis based on available data, but final trading decisions are always yours. Always do your own due diligence.</Callout>
          <H3>Stock Screener with Natural Language</H3>
          <P>The Stock Screener panel also uses AI. Instead of filling in 20 form fields, describe what you're looking for: "Show me mid-cap tech stocks with RSI below 40 and positive earnings growth" and it builds the screen for you.</P>
        </section>

        {/* PORTFOLIO */}
        <section id="portfolio" ref={setRef('portfolio')} style={{ ...sectionStyle, display: sectionVisible('portfolio') ? undefined : 'none' }}>
          <H2>Portfolio Tracking</H2>
          <P>The Portfolio Intelligence panel tracks your positions with AI-generated daily briefings.</P>
          <H3>Adding Positions</H3>
          <UL>
            <LI><B>Manual entry</B>: add symbol, quantity, entry price, entry date</LI>
            <LI><B>CSV import</B>: export from your broker and import here</LI>
          </UL>
          <H3>Metrics Tracked</H3>
          <UL>
            <LI>Unrealized P&L per position and portfolio-wide</LI>
            <LI>Sector allocation</LI>
            <LI>Daily movers within your portfolio</LI>
            <LI>Position-specific news alerts</LI>
          </UL>
          <H3>AI Portfolio Brief</H3>
          <P>Every day, the AI generates a concise briefing on your portfolio: what moved, why, news affecting your holdings, and any red flags across your positions. This runs automatically. Check it in the morning.</P>
          <Callout type="warn" title="Important">FlockTrade does not execute trades. Portfolio tracking is for analysis only. To place orders, use your broker directly.</Callout>
        </section>

        {/* PANEL REFERENCE */}
        <section id="panel-reference" ref={setRef('panel-reference')} style={{ ...sectionStyle, display: sectionVisible('panel-reference') ? undefined : 'none' }}>
          <H2>Complete Panel Reference</H2>
          <P>The terminal includes 90+ panels organized by category. Here's the full list.</P>
          {PANEL_SECTIONS.map(sec => (
            <div key={sec.title}>
              <H3>{sec.title}</H3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12, margin: '20px 0' }}>
                {sec.panels.map(p => <PanelCard key={p.n} name={p.n} desc={p.d} />)}
              </div>
            </div>
          ))}
        </section>

        {/* SHORTCUTS */}
        <section id="shortcuts" ref={setRef('shortcuts')} style={{ ...sectionStyle, display: sectionVisible('shortcuts') ? undefined : 'none' }}>
          <H2>Keyboard Shortcuts</H2>
          <P>On Mac, use <Kbd>Cmd</Kbd> instead of <Kbd>Ctrl</Kbd>.</P>
          {SHORTCUTS.map(group => (
            <div key={group.group}>
              <H3>{group.group}</H3>
              <table style={{ width: '100%', borderCollapse: 'collapse', margin: '20px 0' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '10px 16px', borderBottom: `1px solid ${C.border}`, color: C.textDim, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>Shortcut</th>
                    <th style={{ textAlign: 'left', padding: '10px 16px', borderBottom: `1px solid ${C.border}`, color: C.textDim, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {group.rows.map((row, i) => (
                    <tr key={i}>
                      <td style={{ padding: '10px 16px', borderBottom: `1px solid ${C.border}`, fontSize: 13, width: 180, whiteSpace: 'nowrap' }}>
                        {row.keys.map((k, j) => <React.Fragment key={j}>{j > 0 && ' + '}<Kbd>{k}</Kbd></React.Fragment>)}
                      </td>
                      <td style={{ padding: '10px 16px', borderBottom: `1px solid ${C.border}`, fontSize: 13 }}>{row.action}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </section>

        {/* NOTIFICATIONS */}
        <section id="notifications" ref={setRef('notifications')} style={{ ...sectionStyle, display: sectionVisible('notifications') ? undefined : 'none' }}>
          <H2>Notifications</H2>
          <P>The terminal has a comprehensive notification system. Notifications can be delivered through four channels, and you choose which ones are active for each type of alert.</P>
          <H3>Delivery Methods</H3>
          <UL>
            <LI><B>Toast</B>: brief bottom-right popup that auto-dismisses after 5 seconds (8 for critical)</LI>
            <LI><B>Banner</B>: sticky bar between ticker and workspace tabs</LI>
            <LI><B>Notification Center</B>: persistent log accessible via the 🔔 icon</LI>
            <LI><B>Browser Push</B>: native browser notifications that appear even when the terminal is in another tab</LI>
          </UL>
          <H3>Alert Triggers</H3>
          <P>Each of these can be individually enabled and routed to your chosen delivery methods:</P>
          <UL>
            <LI>Breaking news (from Intelligence Wire and Newsdesk)</LI>
            <LI>Price alerts (set from Watchlist or Chart)</LI>
            <LI>Ticker-specific news</LI>
            <LI>Economic calendar events (HIGH impact, 15 min before)</LI>
            <LI>DEFCON changes</LI>
            <LI>Earthquake M6.0+</LI>
            <LI>Portfolio P&L (configurable threshold)</LI>
          </UL>
          <H3>Quiet Hours</H3>
          <P>Configure a time range when toast and push notifications are suppressed. Notification center still receives them. Overnight ranges (e.g., 22:00 to 07:00) are supported.</P>
          <H3>Notification Center</H3>
          <P>Click the 🔔 icon to open the notification center. Filter by category (All, Breaking, Price, Calendar, System), see them grouped by time (Last hour, Today, Yesterday, Older), and mark as read or clear all. Up to 200 notifications are kept.</P>
        </section>

        {/* SETTINGS */}
        <section id="settings" ref={setRef('settings')} style={{ ...sectionStyle, display: sectionVisible('settings') ? undefined : 'none' }}>
          <H2>Settings</H2>
          <P>Click the <B>⚙️</B> icon in the top bar to open the settings panel. Five sections:</P>
          <H3>Profile & Account</H3>
          <P>Your avatar, display name, Discord connection status, membership badge, and links to edit your profile or manage your subscription.</P>
          <H3>Terminal Settings</H3>
          <UL>
            <LI><B>Default workspace</B>: what opens when you log in</LI>
            <LI><B>Font size</B>: Compact / Normal / Large</LI>
            <LI><B>Grid density</B>: 3 to 6 columns</LI>
            <LI><B>Animations</B>: toggle UI transitions</LI>
          </UL>
          <H3>Alerts & Notifications</H3>
          <P>Configure delivery methods and which triggers use them. Set quiet hours. See <A href="#notifications">Notifications</A> for details.</P>
          <H3>Data & Privacy</H3>
          <UL>
            <LI>Export your full configuration as JSON</LI>
            <LI>Import a configuration from another device</LI>
            <LI>Clear cache</LI>
            <LI>Reset to defaults</LI>
          </UL>
          <H3>Keyboard Shortcuts</H3>
          <P>Read-only reference of all shortcuts.</P>
        </section>

        {/* FAQ */}
        <section id="faq" ref={setRef('faq')} style={{ ...sectionStyle, display: sectionVisible('faq') ? undefined : 'none' }}>
          <H2>Frequently Asked Questions</H2>
          <H3>Can I use the terminal on mobile?</H3>
          <P>No. FlockTrade Terminal is built for desktop and laptop screens only. The density of information and the drag-to-resize workflow don't translate to mobile. Use a computer with a screen of at least 13 inches for the best experience.</P>
          <H3>How often is the data updated?</H3>
          <P>Chart prices and scanner alerts stream in real time (tick-level). Watchlist prices update every 30 seconds during market hours. News feeds update as articles are published. The Country Instability Index updates every 15 minutes. Earnings and economic calendar data update multiple times per day.</P>
          <H3>Can I share my custom scans or workspaces with others?</H3>
          <P>Yes. Use Settings → Data & Privacy → Export to create a JSON file. Share it with anyone. They can import it from the same menu.</P>
          <H3>Does the terminal execute trades?</H3>
          <P>No. FlockTrade is an analysis and intelligence platform. To place orders, use your broker directly. Several panels link to external brokers for quick order entry.</P>
          <H3>Why is the scanner showing delayed data sometimes?</H3>
          <P>The scanner uses tick-level data during regular market hours. Outside regular hours (premarket, after-hours, weekends), data may have slight delays depending on exchange rules. Some premarket scans still work in real time because they rely on extended hours data.</P>
          <H3>How do I report a bug?</H3>
          <P>Click the yellow <B>REPORT BUG</B> button in the top bar. The form auto-captures your current workspace and active panels so we have context. Describe what you were doing and what went wrong.</P>
          <H3>Is my data private?</H3>
          <P>Yes. Your watchlists, custom scans, alerts, and settings are stored privately in your account. We do not share individual user data with third parties. Aggregate anonymized usage metrics help us improve the product.</P>
        </section>

        {/* TROUBLESHOOTING */}
        <section id="troubleshooting" ref={setRef('troubleshooting')} style={{ ...sectionStyle, display: sectionVisible('troubleshooting') ? undefined : 'none' }}>
          <H2>Troubleshooting</H2>
          <H3>The ONLINE badge is red or yellow</H3>
          <P>Your WebSocket connection isn't stable. Check your internet connection. If it persists, try refreshing the page. If that doesn't fix it, clear the terminal cache (Settings → Data & Privacy → Clear cache) and log in again.</P>
          <H3>Prices aren't updating</H3>
          <P>First check the ONLINE badge. If it's green, prices should be flowing. Try switching workspaces and back. This re-subscribes the data stream. If symbols are still stale, refresh the page.</P>
          <H3>A panel is blank or stuck loading</H3>
          <P>Close the panel and add it back. If that doesn't work, the data source may be temporarily unavailable. Check if the same panel works on another workspace or for another symbol.</P>
          <H3>My custom workspace disappeared</H3>
          <P>Customizations are saved to your account, so they persist across devices. If something looks lost, try logging out and back in. If you previously exported your configuration, you can re-import it from Settings → Data & Privacy.</P>
          <H3>Scanner alerts stopped firing</H3>
          <P>Make sure at least one scanner panel is open and running. Check that the market is open. Most strategies only run during regular hours. Some strategies (premarket, afterhours) only run in those specific windows.</P>
          <H3>Notification sounds aren't playing</H3>
          <P>Browser autoplay policies require user interaction before sound can play. Click anywhere in the terminal once after loading. Also check Settings → Alerts to confirm sounds are enabled.</P>
        </section>

        {/* SUPPORT */}
        <section id="support" ref={setRef('support')} style={{ ...sectionStyle, display: sectionVisible('support') ? undefined : 'none' }}>
          <H2>Contact Support</H2>
          <P>If you need help, the fastest ways to reach us:</P>
          <UL>
            <LI><B>Report Bug button</B>: fastest way to report technical issues, auto-captures context</LI>
            <LI><B>Discord</B>: join the FlockTrade community Discord for peer help and staff support</LI>
            <LI><B>Email</B>: <span style={{ color: C.orange }}>support@flocktrade.com</span></LI>
          </UL>
          <H3>When reporting a problem, include:</H3>
          <UL>
            <LI>What you were trying to do</LI>
            <LI>What actually happened</LI>
            <LI>Which workspace and panels were open</LI>
            <LI>Any error messages (screenshot helps)</LI>
            <LI>Your browser and operating system</LI>
          </UL>
          <Callout type="tip">The Report Bug form in the top bar auto-captures most of this information for you. Use it when you can.</Callout>
        </section>

        {/* FOOTER */}
        <footer style={{ marginTop: 80, padding: '32px 0', borderTop: `1px solid ${C.border}`, color: C.textDim, fontSize: 12, textAlign: 'center' }}>
          <p>FlockTrade AS · Bod&oslash;, Norway · Org.nr 929 719 824</p>
          <p style={{ marginTop: 8 }}>Documentation last updated April 2026</p>
        </footer>
      </main>

      {/* Responsive styles via a style tag */}
      <style>{`
        @media (max-width: 900px) {
          .docs-sidebar-toggle { display: block !important; }
        }
      `}</style>
    </div>
  );
};

export default Docs;
